/**
 * helpers.js
 * desc: utilities for internationalization of translated languages
 */

import { getQueryStringObject } from '../helpers/utils';

// Language Codes: Codes for the Representation of Names of Languages (ISO 639-1 Code)
// https://www.loc.gov/standards/iso639-2/php/code_list.php
// Some languages only have three letter codes (ISO 639-2 Code)
//
// Country Codes: Country ISO codes (ALPHA-2)
// https://www.iban.com/country-codes

const locales = {
  DEFAULT: "en-US", // Default: English US
  SUPPORTED: [
    "af-ZA",  // Afrikaans - South Africa
    "af",     // Afrikaans
    "ar-IL",  // Arabic - Israel
    "ar",     // Arabic
    "as",     // Assamese
    "as-IN",  // Assamese - India
    "bg-BG",  // Bulgarian - Bulgaria
    "bg",     // Bulgarian
    "bs",     // Bosnian
    "bs-BA",  // Bosnian - Bosnia and Herzegovina
    "bn",     // Bengali
    "bn-IN",  // Bengali - India
    "ca",     // Catalan
    "ca-ES",  // Catalan - Spain
    "ceb-PH", // Cebuano - Philippines
    "ceb",    // Cebuano
    "cs-CZ",  // Czech - Czech Republic
    "cs",     // Czech
    "da-DK",  // Dutch - Denmark
    "da",     // Dutch
    "de-AT",  // German - Austria
    "de-DE",  // German - Germany
    "de-DK",  // German - Denmark
    "de",     // German
    "el",     // Greek
    "el-Gr",  // Greek - Greece
    "en-AU",  // English - Australia
    "en-GB",  // English - United Kingdom
    "en-IN",  // English - India
    "en-HK",  // English - Hong Kong
    "en-MY",  // English - Malaysia
    "en-NZ",  // English - New Zealand
    "en-PH",  // English - Philippines
    "en-SG",  // English - Singapore
    "en-US",  // English - United States
    "en-UK",  // English - United Kingdom
    "en-ZA",  // English - South Africa
    "en",     // English
    "es-ES",  // Spanish - Spain
    "es-US",  // Spanish - United States
    "es",     // Spanish
    "es-AR",  // Spanish - Argentina
    "es-CL",  // Spanish - Chile
    "es-CO",  // Spanish - Colombia
    "es-MX",  // Spanish - Mexico
    "es-PE",  // Spanish - Peru
    "et",     // Estonian
    "et-EE",  // Estonian - Estonia
    "fi-FI",  // Finnish - Finland
    "fi",     // Finnish
    "fil-PH", // Filipino (Tagalog) - Philippines
    "fil",    // Filipino (Tagalog)
    "fr-BE",  // French - Belgium
    "fr-CA",  // French - Canada
    "fr-FR",  // French - France
    "fr",     // French
    "gu-IN",  // Gujarati - India
    "gu",     // Gujarati
    "id-ID",  // Bahasa - Indonesia
    "id",     // Bahasa
    "it-IT",  // Italian - Italy
    "it",     // Italian
    "hi-IN",  // Hindi - India
    "hi",     // Hindi
    "he-IL",  // Hebrew - Israel
    "he",     // Hebrew
    "hil-PH", // Hiligaynon - Philippines
    "hil",    // Hiligaynon
    "hr",     // Croatian
    "hr-HR",  // Croatian - Croatia
    "hu-HU",  // Hungarian - Hungary
    "hu",     // Hungarian
    "ja-JP",  // Japanese [chrome uses JA] - Japan
    "ja",     // Japanese [chrome uses JA]
    "ka-GE",  // Georgian - Georgia
    "ka",     // Georgian
    "kn-IN",  // Kannada - India
    "kn",     // Kannada
    "ko-KR",  // Korean - Korea (the Republic of)
    "ko",     // Korean
    "lt",     // Lithuanian
    "lt-LT",  // Lithuanian - Lithuania
    "ml-IN",  // Malayalam - India
    "ml",     // Malayalam
    "mk",     // Macedonian
    "mk-ML",  // Macedonian - Moldova 
    "mo",
    "mr-IN",  // Marathi - India
    "mr",     // Marathi
    "ms-MY",  // Malay - Malaysia
    "ms",     // Malay
    "nl",     // Dutch
    "nl-BE",  // Dutch - Belgium
    "nl-NL",  // Dutch - Netherlands
    "no-NO",  // Norwegian - Norway
    "no",     // Norwegian
    "nso",    // Northern Sotho
    "nso-ZA", // Northern Sotho - South Africa
    "or",     // Odia
    "or-IN",  // Odia - India
    "pa",     // Punjabi
    "pa-IN",  // Punjabi - India
    "pl-PL",  // Polish - Poland
    "pl",     // Polish
    "pt-BR",  // Portuguese - Brazil
    "pt",     // Portuguese
    "ro-RO",  // Romanian - Romania
    "ro",     // Romanian
    "ro-MD",  // Moldova Romanian (Moldavian)
    "ru-ET",  // Russian - Estonia
    "ru-IL",  // Russian - Israel
    "ru-LT",  // Russian - Lithuania
    "ru-MD",  // Russian - Moldova
    "ru",     // Russian
    "ru-EE",  // Russian Estonian
    "ru-LT",  // Russian Lithania
    // "sn",     // Shona
    "sr-Cyrl-RS",  // Serbian (Cyrillic) - Serbia
    "sr-RS",  // Serbian - Serbia
    "sr",     // Serbian
    "st-ZA",  // Sotho - South Africa
    "st",     // Sotho
    "sot-ZA",  // Sotho - South Africa
    "sot",     // Sotho
    "sv-SE",  // Swedish - Sweden
    "sv",     // Swedish
    "ta-IN",   // Tamil - India
    "ta",     // Tamil
    "ta-MY",  // Malaysia Tamil
    "te-IN",  // Telugu - India
    "te",     // Telugu
    "tgl-PH", // Tagalog - Philippines
    "tgl",    // Tagalog
    "th-TH",  // Thai - Thailand
    "th",     // Thai
    "tr-TR",  // Turkish - Turkey
    "tr",     // Turkish
    "uk",     // Ukrainian
    "uk-PL",  // Ukrainian - Poland
    "uk-CZ",  // Ukrainian - Czech Republic
    "ur-IN",  // Urdu - India
    "ur",     // Urdu
    "xh-ZA",  // Xhosa - South Africa
    "xh",     // Xhosa
    "zh-CN",  // Chinese - China
    "zh-US",  // Chinese - United States
    "zh-TW",  // Chinese - Taiwan
    "zh-SG",  // Chinese - Singapore
    "zh-HK",  // Chinese - Hong Kong
    "zh",     // Chinese
    "zu-ZA",  // Zulu - South Africa
    "zu",      // Zulu
    "zh-MY",  // Chinese - Malaysia
  ]
}

 /**
  * getSupportedDictionary
  * desc: returns available data file
  * @param  {string} locale   uses a validated locale string
  * @param  {string} path  relevant path of data
  * @return {obj}       locale object
  */
 export async function getSupportedDictionary(locale, path) {
  const language = locale.split('-')[0];
  let localeFile, languageFile, defaultFile;

  // Try to load local and language files
  try {
    localeFile = await import(`./locales/${path}/${locale}.json`);
    languageFile = await import(`./locales/${path}/${language}.json`);
  }
  catch (e) {
    if (e instanceof Error && e.code === "MODULE_NOT_FOUND") {
      console.log(e.message);
    } else {
      throw e;
    }
  }

  defaultFile = await import(`./locales/${path}/${locales.DEFAULT}.json`);

  if (!!localeFile) {
    return localeFile;
  } else if (!!languageFile) {
    return languageFile;
  } else {
    return defaultFile;
  }
}


/**
  * getLocale
  * desc: returns available locale
  * @return {string}       locale string
  */
export function getLocale() {
 const qsObj = getQueryStringObject();
 const userLocale = (qsObj.locale) ? qsObj.locale : navigator.language ? navigator.language : 'en-us';

 // check if user locale supported
 function localeFound(locale) {
   return locales.SUPPORTED.indexOf(locale) !== -1
 }
 const language = userLocale.split('-')[0];
 // Set locale to complete local, langauge, or default
 return localeFound(userLocale) ? userLocale : localeFound(language) ? language : locales.DEFAULT;
}
